import axios from "axios";
import Config from "../config.js";
import { tokenStorage } from "../utils/tokenStorage";

class ApiService {
  constructor() {
    this.setupInterceptors();
  }

  setupInterceptors() {
    // Request interceptor to add token to all requests
    axios.interceptors.request.use(
      async (config) => {
        const token = await tokenStorage.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response interceptor for token refresh
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('auth/refresh')) {
          originalRequest._retry = true;

          try {
            const result = await this.refreshToken();
            if (result.success) {
              // Update the authorization header for the original request
              originalRequest.headers.Authorization = `Bearer ${result.data.access_token}`;
              return axios(originalRequest);
            }
          } catch (refreshError) {
            console.error("Token refresh failed:", refreshError);
            this.logout();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      },
    );
  }

  setLogoutFunction(logoutFn) {
    this.logout = logoutFn;
  }

  setSessionExpiredFunction(fn) {
    this.onSessionExpired = fn;
  }

  async refreshToken() {
    try {
      const refreshToken = await tokenStorage.getRefreshToken();
      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      const response = await axios.post(`${Config.baseUrl}auth/refresh`, {
        refresh_token: refreshToken,
      });

      const { access_token, refresh_token: newRefreshToken } = response.data;

      if (access_token) {
        await tokenStorage.setTokens(access_token, newRefreshToken || refreshToken);
        return { success: true, data: response.data };
      } else {
        throw new Error("Invalid token refresh response");
      }
    } catch (error) {
      console.error("Failed to refresh token:", error);
      // 401 on refresh endpoint means the refresh token itself is expired
      if (error.response?.status === 401 && this.onSessionExpired) {
        this.onSessionExpired();
      } else {
        this.logout();
      }
      return { success: false, error: "Session expired. Please log in again." };
    }
  }

  async post(path, body) {
    try {
      const response = await axios.post(`${Config.baseUrl}${path}`, body, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      return response;
    } catch (error) {
      console.error(`POST ${path} failed:`, error);
      throw error;
    }
  }

  async get(path, params = null) {
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
        },
      };

      if (params) {
        config.params = params;
      }

      const response = await axios.get(`${Config.baseUrl}${path}`, config);
      return response;
    } catch (error) {
      console.error(`GET ${path} failed:`, error);
      throw error;
    }
  }

  async patch(path, body) {
    try {
      const response = await axios.patch(`${Config.baseUrl}${path}`, body, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      return response;
    } catch (error) {
      console.error(`PATCH ${path} failed:`, error);
      throw error;
    }
  }

  async put(path, body = {}, customConfig = {}) {
    try {
      const response = await axios.put(
        `${Config.baseUrl}${path}`,
        body,
        {
          headers: {
            "Content-Type": "application/json",
          },
          ...customConfig,
        },
      );
      return response;
    } catch (error) {
      console.error(`PUT ${path} failed:`, error);
      throw error;
    }
  }

  async delete(path, data = null) {
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
        },
      };
      if (data) {
        config.data = data;
      }
      const response = await axios.delete(`${Config.baseUrl}${path}`, config);
      return response;
    } catch (error) {
      console.error(`DELETE ${path} failed:`, error);
      throw error;
    }
  }
}

const apiService = new ApiService();

export default apiService;
